import type { ImageProvider } from '@vp/render';
import { storage } from '../platform';

/** Decoded image cache for `att:<sha256>` sources; invalidates the renderer when an image is ready. */
export class AttachmentImages implements ImageProvider {
  private cache = new Map<string, ImageBitmap | null>();
  private loading = new Set<string>();

  constructor(private readonly onReady: () => void) {}

  get(src: string): ImageBitmap | null {
    const hit = this.cache.get(src);
    if (hit !== undefined) return hit;
    if (!this.loading.has(src)) void this.load(src);
    return null;
  }

  private async load(src: string): Promise<void> {
    this.loading.add(src);
    try {
      const hash = src.startsWith('att:') ? src.slice(4) : src;
      const blob = await storage.getAttachment(hash);
      this.cache.set(src, blob ? await createImageBitmap(blob) : null);
    } catch {
      this.cache.set(src, null);
    } finally {
      this.loading.delete(src);
      this.onReady();
    }
  }

  clear(): void {
    for (const b of this.cache.values()) b?.close();
    this.cache.clear();
  }
}
